import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import { API_URL } from '../config';
import { getFreshSupabaseToken } from '../tokenRefresh';

function NotificationBell({ userId }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Auth headers for all API calls (includes the RLS login card, auto-refreshed)
  const getAuthHeaders = async () => {
    const token = localStorage.getItem('token');
    const supabaseToken = await getFreshSupabaseToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (supabaseToken) headers['x-supabase-token'] = supabaseToken;
    return headers;
  };

  // Wrap fetchNotifications in useCallback so it doesn't change on every render
  const fetchNotifications = useCallback(async () => {
    if (!userId) {
      console.log('⚠️ No userId provided to NotificationBell');
      return;
    }
    try {
      console.log('🔔 Fetching notifications for user:', userId);
      const response = await fetch(`${API_URL}/notifications?user_id=${userId}`, {
        headers: await getAuthHeaders()
      });
      const data = await response.json();
      console.log('📥 Notifications response:', data);
      
      if (data.success) {
        setNotifications(data.notifications || []);
        const unread = data.notifications.filter(n => !n.is_read).length;
        setUnreadCount(unread);
        console.log(`🔔 Found ${data.notifications.length} notifications, ${unread} unread`);
      } else {
        console.error('❌ Failed to fetch notifications:', data.error);
      }
    } catch (err) {
      console.error('❌ Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [userId, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: await getAuthHeaders()
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/notifications/read-all?user_id=${userId}`, {
        method: 'PUT',
        headers: await getAuthHeaders()
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    setIsOpen(false);
    
    if (notification.link) {
      navigate(notification.link);
    } else {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user?.role === 'subcontractor' || user?.role === 'subbie') {
        navigate('/subbie-notifications');
      } else if (user?.role === 'client') {
        navigate('/client-notifications');
      } else {
        navigate('/notifications');
      }
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.role === 'subcontractor' || user?.role === 'subbie') {
      navigate('/subbie-notifications');
    } else if (user?.role === 'client') {
      navigate('/client-notifications');
    } else {
      navigate('/notifications');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'job_request': '📋',
      'job_allocated': '🚛',
      'subbie_response': '📝',
      'job_approved': '✅',
      'job_completed': '🏁',
      'job_rejected': '❌',
      'new_job': '📋',
      'fleet_allocated': '🚛',
      'fleet_approved': '✅',
      'info': 'ℹ️',
      'warning': '⚠️'
    };
    return icons[type] || '🔔';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'just now';
    const now = new Date();
    const then = new Date(timestamp);
    const diff = Math.floor((now - then) / 1000);
    
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        style={{ color: '#0A1929' }}
      >
        <FaBell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs font-bold rounded-full" style={{ backgroundColor: '#EF4444', color: 'white', minWidth: '18px' }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border z-50 max-h-96 overflow-y-auto" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex justify-between items-center p-3 border-b" style={{ borderColor: '#E8ECEF' }}>
            <span className="font-medium text-sm" style={{ color: '#0A1929' }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs hover:underline" style={{ color: '#00A86B' }}
              >
                Mark all read
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-4 text-center">
              <span className="text-sm" style={{ color: '#94A3B8' }}>Loading...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-3xl mb-2">🔔</div>
              <p className="text-sm" style={{ color: '#94A3B8' }}>No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: '#E8ECEF' }}>
              {notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`p-3 hover:bg-gray-50 transition-colors cursor-pointer ${!notification.is_read ? 'bg-blue-50' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-xl">{getNotificationIcon(notification.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: '#0A1929' }}>{notification.title}</p>
                      <p className="text-xs" style={{ color: '#4A5568' }}>{notification.message}</p>
                      <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{formatTime(notification.created_at)}</p>
                    </div>
                    {!notification.is_read && (
                      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: '#3B82F6' }}></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="p-2 border-t" style={{ borderColor: '#E8ECEF' }}>
            <button
              onClick={handleViewAll}
              className="w-full text-center text-sm text-green-600 hover:text-green-700 py-1.5 rounded-lg hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
            >
              <FaBell size={14} /> View All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;